import type { UiDestination } from '@/hooks/useDestinations';
import type {
  SkillLevel,
  SurfGoal,
  TripVibe,
  Budget,
  WaterTemp,
  CrowdTolerance,
} from './planner-vocabulary';
import {
  getSuitabilityForAbility,
  isQuieterThanPeakSeason,
  isShoulderSeasonMonth,
  type MonthlyIntelligenceRow,
} from './destination-monthly-intelligence';

// --- Types ---------------------------------------------------------------

export type { SkillLevel, SurfGoal, TripVibe, Budget, WaterTemp, CrowdTolerance };

// experienceStyle (Comfortable & Easy / Balanced / Adventure) was retired
// from the user-facing Trip Finder -- Comfortable/Adventure now overlap
// with tripVibe's Relaxed/Remote & Adventurous, and Balanced was a neutral
// default rather than a distinct signal. The underlying p_experience_style
// SQL parameter and experience_score branch are deliberately left in place
// (see the SQL migration for why) -- this app no longer sends a value for
// it, and RecommendationPrefs no longer carries the field.
export interface RecommendationPrefs {
  skillLevel: SkillLevel;
  /** What the surfer wants from the surfing itself. */
  surfGoal: SurfGoal;
  /** What atmosphere they want around the surfing -- independent of
   * surfGoal, not a duplicate signal. */
  tripVibe: TripVibe;
  month: string;
  budget: Budget;
  waterTemp: WaterTemp;
  crowdTolerance: CrowdTolerance;
}

export interface Reason {
  key: string;
  priority: number;
  /** Ultra-short tag copy for compact chip display. */
  label: string;
  /** Full sentence for the expanded "see why" view. */
  text: string;
}

// --- Derived context -----------------------------------------------------

type SkillMatch = 'below' | 'in' | 'above' | 'unknown';
type SeasonMatch = 'peak' | 'shoulder' | 'off' | 'unknown';
type BudgetCmp = 'under' | 'exact' | 'over';

interface DerivedContext {
  skillMatch: SkillMatch;
  skillMin: SkillLevel | null;
  skillMax: SkillLevel | null;
  seasonMatch: SeasonMatch;
  budgetCmp: BudgetCmp;
  crowdBucket: 'quiet' | 'moderate' | 'busy' | 'unknown';
  /** The resolved row for the user's selected travel month, only when the
   * destination is in the ~55-destination V1 monthly-intelligence set and has
   * a row for that month. Every monthly-informed rule below must treat this as
   * optional and degrade to no-op (not throw, not fall back to a guess) when
   * it's null -- that's the fallback for destinations outside the dataset. */
  monthlyRow: MonthlyIntelligenceRow | null;
  monthlyAllRows: MonthlyIntelligenceRow[];
  /** True only when seasonMatch === 'shoulder' was decided from real
   * shoulder_season_value_1_10 data, not the adjacent-best-month heuristic --
   * lets copy be more confident when it's backed by research. */
  monthlyShoulderConfirmed: boolean;
}

// --- Helpers -------------------------------------------------------------

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const monthIndex = (m: string): number => {
  if (!m) return -1;
  return MONTHS.findIndex((x) => x.toLowerCase() === m.slice(0, 3).toLowerCase());
};

const isMonthInBest = (month: string, bestMonths: string[]): boolean => {
  const target = month.slice(0, 3).toLowerCase();
  return bestMonths.some((b) => b.slice(0, 3).toLowerCase() === target);
};

const isMonthAdjacent = (month: string, bestMonths: string[]): boolean => {
  const idx = monthIndex(month);
  if (idx < 0) return false;
  const prev = MONTHS[(idx + 11) % 12];
  const next = MONTHS[(idx + 1) % 12];
  return isMonthInBest(prev, bestMonths) || isMonthInBest(next, bestMonths);
};

const budgetToNum = (b: Budget | string | null | undefined): number => {
  if (!b) return 2;
  const dollars = (b.match(/\$/g) || []).length;
  return dollars || 2;
};

const SKILL_ORDER: SkillLevel[] = ['beginner', 'intermediate', 'advanced', 'expert'];
const skillIdx = (s: SkillLevel) => SKILL_ORDER.indexOf(s);

const hasVibe = (dest: UiDestination, needles: string[]): boolean => {
  const tags = (dest.vibeTags || []).map((v) => v.toLowerCase());
  return tags.some((t) => needles.some((n) => t.includes(n)));
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const bucketCrowd = (c: UiDestination['crowdLevel']): DerivedContext['crowdBucket'] => {
  if (c === 'empty' || c === 'quiet') return 'quiet';
  if (c === 'busy' || c === 'crowded') return 'busy';
  if (c === 'moderate') return 'moderate';
  return 'unknown';
};

const deriveContext = (
  dest: UiDestination,
  prefs: RecommendationPrefs,
  monthlyRows: MonthlyIntelligenceRow[] = [],
): DerivedContext => {
  const levels = dest.skillLevels || [];
  let skillMatch: SkillMatch = 'unknown';
  let skillMin: SkillLevel | null = null;
  let skillMax: SkillLevel | null = null;
  if (levels.length) {
    const mins = Math.min(...levels.map(skillIdx));
    const maxs = Math.max(...levels.map(skillIdx));
    skillMin = SKILL_ORDER[mins];
    skillMax = SKILL_ORDER[maxs];
    const u = skillIdx(prefs.skillLevel);
    if (u < mins) skillMatch = 'below';
    else if (u > maxs) skillMatch = 'above';
    else skillMatch = 'in';
  }

  const targetMonthIdx = monthIndex(prefs.month); // 0-based, or -1
  const monthlyRow = targetMonthIdx >= 0 ? monthlyRows.find((r) => r.month === targetMonthIdx + 1) ?? null : null;

  // Shoulder-season classification: prefer the real researched signal when this
  // destination has monthly intelligence for the selected month; fall back to
  // the adjacent-best-month heuristic only when it doesn't (the ~12 destinations
  // still outside the V1 research set, or a month with no monthly row).
  let seasonMatch: SeasonMatch = 'unknown';
  let monthlyShoulderConfirmed = false;
  if (dest.bestMonths?.length || monthlyRow) {
    if (isMonthInBest(prefs.month, dest.bestMonths || [])) {
      seasonMatch = 'peak';
    } else if (monthlyRow) {
      if (isShoulderSeasonMonth(monthlyRow)) {
        seasonMatch = 'shoulder';
        monthlyShoulderConfirmed = true;
      } else {
        seasonMatch = 'off';
      }
    } else if (isMonthAdjacent(prefs.month, dest.bestMonths || [])) {
      seasonMatch = 'shoulder';
    } else {
      seasonMatch = 'off';
    }
  }

  const destCost = dest.costRating;
  const userCost = budgetToNum(prefs.budget);
  const budgetCmp: BudgetCmp =
    destCost === userCost ? 'exact' : destCost < userCost ? 'under' : 'over';

  return {
    skillMatch,
    skillMin,
    skillMax,
    seasonMatch,
    budgetCmp,
    crowdBucket: bucketCrowd(dest.crowdLevel),
    monthlyRow,
    monthlyAllRows: monthlyRows,
    monthlyShoulderConfirmed,
  };
};

// --- Positive rules ------------------------------------------------------

type Rule = (dest: UiDestination, prefs: RecommendationPrefs, ctx: DerivedContext) => Reason | null;

const positiveRules: Rule[] = [
  (_dest, _prefs, ctx) => {
    if (ctx.seasonMatch === 'peak') {
      return {
        key: 'season-peak', priority: 100,
        label: 'Peak season',
        text: "You're travelling during one of its most consistent seasons",
      };
    }
    if (ctx.seasonMatch === 'shoulder') {
      return ctx.monthlyShoulderConfirmed
        ? {
            key: 'season-shoulder', priority: 68,
            label: 'Shoulder season',
            text: 'Strong shoulder-season value this month — a good trade-off without peak crowds',
          }
        : {
            key: 'season-shoulder', priority: 65,
            label: 'Shoulder season',
            text: 'Your travel window catches the shoulder of its main season',
          };
    }
    return null;
  },

  // The four rules below only fire for the ~55 destinations with monthly
  // intelligence for the selected month (ctx.monthlyRow); destinations outside
  // that set simply don't get these chips, falling back to the static rules
  // elsewhere in this file -- no guess is substituted in their place.
  (_dest, prefs, ctx) => {
    if (!ctx.monthlyRow) return null;
    const score = getSuitabilityForAbility(ctx.monthlyRow, prefs.skillLevel);
    if (score !== null && score >= 7) {
      return {
        key: 'monthly-ability-good', priority: 82,
        label: `Good month for ${prefs.skillLevel}s`,
        text: `A good month specifically for ${prefs.skillLevel} surfers here`,
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (!ctx.monthlyRow) return null;
    if (ctx.monthlyRow.surfConsistencyScore !== null && ctx.monthlyRow.surfConsistencyScore >= 8) {
      return {
        key: 'monthly-consistency', priority: 74,
        label: 'Consistent this month',
        text: 'Consistent surf this month, based on researched monthly data',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (!ctx.monthlyRow) return null;
    if (isQuieterThanPeakSeason(ctx.monthlyRow, ctx.monthlyAllRows)) {
      return {
        key: 'monthly-quieter', priority: 57,
        label: 'Quieter than peak',
        text: 'Usually quieter than peak season here',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (!ctx.monthlyRow) return null;
    if (ctx.monthlyRow.comfortScore !== null && ctx.monthlyRow.comfortScore >= 7) {
      return {
        key: 'monthly-comfort', priority: 56,
        label: 'Comfortable conditions',
        text: 'Warmer, more comfortable conditions this month',
      };
    }
    return null;
  },

  (dest, prefs, ctx) => {
    if (ctx.skillMatch !== 'in') return null;
    const levels = dest.skillLevels || [];
    if (levels.includes(prefs.skillLevel)) {
      // The static tag can't contradict real, month-specific research: when
      // this destination has monthly intelligence for the searched month, a
      // poor fit that month (<4/10, the same floor the RPC's monthly-ability
      // penalty uses) suppresses this chip instead of claiming "well suited"
      // for a month the ability curve says otherwise -- monthly-harder-than-
      // selected below carries that message instead.
      const monthlyScore = ctx.monthlyRow ? getSuitabilityForAbility(ctx.monthlyRow, prefs.skillLevel) : null;
      if (monthlyScore !== null && monthlyScore < 4) return null;
      return {
        key: 'skill-fit', priority: 92,
        label: `${cap(prefs.skillLevel)} friendly`,
        text: `Well suited to ${prefs.skillLevel} surfers`,
      };
    }
    if (ctx.skillMin && ctx.skillMax && ctx.skillMin !== ctx.skillMax) {
      return {
        key: 'skill-range', priority: 80,
        label: `${cap(ctx.skillMin)}–${cap(ctx.skillMax)}`,
        text: `Caters to surfers from ${ctx.skillMin} through to ${ctx.skillMax}`,
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (ctx.budgetCmp === 'exact') {
      return {
        key: 'budget-match', priority: 75,
        label: 'Within your budget',
        text: 'Fits the budget you selected',
      };
    }
    if (ctx.budgetCmp === 'under') {
      return {
        key: 'budget-under', priority: 70,
        label: 'Under budget',
        text: 'Comes in comfortably under the budget you set',
      };
    }
    return null;
  },

  // --- Surf-objective rules (surfGoal) -----------------------------------

  (dest, prefs, ctx) => {
    if (prefs.surfGoal === 'learn' && dest.waveType === 'beach' && ctx.skillMatch !== 'above') {
      return {
        key: 'waves-learn', priority: 88,
        label: 'Beginner-friendly waves',
        text: 'Forgiving beach breaks that suit surfers still learning',
      };
    }
    return null;
  },
  (dest, prefs) => {
    if (prefs.surfGoal === 'score' && (dest.waveType === 'reef' || dest.waveType === 'point')) {
      return {
        key: 'waves-score', priority: 85,
        label: `World-class ${dest.waveType} waves`,
        text: `Known for high-quality ${dest.waveType} waves when it's on`,
      };
    }
    return null;
  },
  (dest, prefs, ctx) => {
    if (
      prefs.surfGoal === 'progress' &&
      dest.consistency >= 7 &&
      ctx.skillMatch !== 'below'
    ) {
      return {
        key: 'waves-progress', priority: 78,
        label: 'Great for progression',
        text: 'Frequent, rideable waves to help you progress quickly',
      };
    }
    return null;
  },
  (dest, prefs) => {
    if (prefs.surfGoal === 'consistent' && dest.consistency >= 7) {
      return {
        key: 'waves-consistent', priority: 78,
        label: 'Reliable surf',
        text: 'One of the more dependable spots for getting in the water often',
      };
    }
    return null;
  },

  (_dest, prefs, ctx) => {
    if (prefs.crowdTolerance === 'lot' && ctx.crowdBucket === 'quiet') {
      return {
        key: 'crowds-quiet', priority: 72,
        label: 'Quiet surf',
        text: 'Line-ups tend to stay uncrowded',
      };
    }
    if (prefs.crowdTolerance === 'none' && ctx.crowdBucket === 'busy') {
      return {
        key: 'crowds-lively', priority: 60,
        label: 'Lively line-ups',
        text: 'A busy, social line-up you said you were happy with',
      };
    }
    if (prefs.crowdTolerance === 'some' && ctx.crowdBucket === 'moderate') {
      return {
        key: 'crowds-mid', priority: 55,
        label: 'Balanced crowds',
        text: 'A comfortable middle ground on crowds',
      };
    }
    return null;
  },

  // --- Trip-vibe rules (tripVibe) — the atmosphere around the surf,
  // independent of surf objective. Backed by real destinations columns
  // (adventureScore, socialSceneScore, travelEaseScore, partyScene,
  // remoteFeel, hasSurfCamps/hasSurfHostels) with a vibeTags fallback for
  // destinations missing a numeric score, never a fabricated proxy. ------

  (dest, prefs) => {
    if (prefs.tripVibe !== 'remote') return null;
    if ((dest.adventureScore !== null && dest.adventureScore >= 7) || dest.remoteFeel || hasVibe(dest, ['remote', 'adventure', 'wild', 'raw'])) {
      return {
        key: 'vibe-remote', priority: 59,
        label: 'Remote & adventurous',
        text: 'A genuine off-the-beaten-track destination for surfers wanting adventure',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (prefs.tripVibe !== 'relaxed') return null;
    if ((dest.travelEaseScore !== null && dest.travelEaseScore >= 7) || hasVibe(dest, ['chill', 'wellness', 'relaxed'])) {
      return {
        key: 'vibe-relaxed', priority: 59,
        label: 'Relaxed atmosphere',
        text: 'Easy-going pace with plenty of room to switch off between sessions',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (prefs.tripVibe !== 'social') return null;
    if (dest.hasSurfCamps || dest.hasSurfHostels || (dest.socialSceneScore !== null && dest.socialSceneScore >= 7) || hasVibe(dest, ['social', 'surf camp'])) {
      return {
        key: 'vibe-social', priority: 59,
        label: 'Social surf scene',
        text: 'Surf camps and hostels make it easy to meet other travelling surfers',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (prefs.tripVibe !== 'party') return null;
    if (dest.partyScene || hasVibe(dest, ['nightlife', 'party'])) {
      return {
        key: 'vibe-party', priority: 59,
        label: 'Good nightlife',
        text: 'A real après-surf social and nightlife scene',
      };
    }
    return null;
  },

  (dest) => {
    if (dest.consistency >= 8) {
      return {
        key: 'consistency-high', priority: 50,
        label: 'Consistent surf',
        text: 'One of the more reliable destinations for consistent surf',
      };
    }
    return null;
  },
];

// --- Trade-off rules -----------------------------------------------------

const tradeoffRules: Rule[] = [
  (_dest, _prefs, ctx) => {
    if (ctx.skillMatch === 'below' && ctx.skillMin) {
      return {
        key: 'skill-too-high', priority: 95,
        label: `Needs ${ctx.skillMin}+`,
        text: `Best once you're comfortable at ${ctx.skillMin} level`,
      };
    }
    return null;
  },

  // Month-specific nuance on top of the destination-wide skill tags above: even
  // a destination generally tagged for the user's level can swing harder in a
  // specific researched month (e.g. a mixed-level spot going full winter-advanced).
  // Only fires for the monthly-intelligence set; degrades to no-op otherwise.
  (_dest, prefs, ctx) => {
    if (!ctx.monthlyRow) return null;
    const score = getSuitabilityForAbility(ctx.monthlyRow, prefs.skillLevel);
    if (score !== null && score <= 4) {
      return {
        key: 'monthly-harder-than-selected', priority: 83,
        label: 'Bigger surf this month',
        text: 'More challenging surf than you selected is typical this month',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (ctx.skillMatch === 'above') {
      return {
        key: 'skill-too-mellow', priority: 90,
        label: 'May feel mellow',
        text: 'May feel mellow if you were hoping for higher-performance waves',
      };
    }
    return null;
  },

  // Reef/point exposure is only surfaced as a trade-off when it has a real
  // consequence for THIS user -- a beginner at a reef-dominant destination --
  // never as blanket destination trivia. "Reef breaks" / "Long paddles" named
  // for their own sake are not useful enough to a traveller deciding where to
  // go, so they're retired as primary tags; this replaces them with the
  // actual consequence.
  (dest, prefs, ctx) => {
    if (prefs.skillLevel === 'beginner' && dest.waveType === 'reef' && ctx.skillMatch !== 'below') {
      return {
        key: 'limited-beginner-waves', priority: 65,
        label: 'Limited beginner-friendly waves',
        text: 'Mostly reef breaks — fewer forgiving options for newer surfers, even though the destination is tagged for your level',
      };
    }
    return null;
  },

  (dest) => {
    if (dest.consistency <= 4) {
      return {
        key: 'consistency-low', priority: 62,
        label: 'Inconsistent surf',
        text: 'Swell here is less reliable than average — expect some flat spells',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (ctx.monthlyRow?.rainDays !== null && ctx.monthlyRow?.rainDays !== undefined && ctx.monthlyRow.rainDays >= 15) {
      return {
        key: 'wet-season', priority: 58,
        label: 'Wet season',
        text: 'Your travel month typically sees frequent rain here',
      };
    }
    return null;
  },

  (_dest, prefs, ctx) => {
    if (ctx.crowdBucket === 'busy' && prefs.crowdTolerance === 'lot') {
      return {
        key: 'crowds-busy', priority: 78,
        label: 'Busy line-ups',
        text: 'Can get crowded when the swell lights up',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (ctx.budgetCmp === 'over') {
      return {
        key: 'cost-high', priority: 68,
        label: 'Above your budget',
        text: 'Sits at the pricier end of the trips you were considering',
      };
    }
    return null;
  },

  (_dest, _prefs, ctx) => {
    if (ctx.seasonMatch === 'off') {
      return {
        key: 'off-season', priority: 85,
        label: 'Not peak season',
        text: 'Less consistent outside its main season, so pack patience',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (
      (dest.remoteFeel || hasVibe(dest, ['remote', 'wild', 'raw'])) &&
      prefs.tripVibe !== 'remote'
    ) {
      return {
        key: 'more-difficult-travel', priority: 60,
        label: 'More difficult travel',
        text: 'Getting around takes some effort — best with a rental or scooter',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (
      prefs.tripVibe === 'social' &&
      !(dest.hasSurfCamps || dest.hasSurfHostels || (dest.socialSceneScore !== null && dest.socialSceneScore >= 7) || hasVibe(dest, ['social']))
    ) {
      return {
        key: 'less-social', priority: 48,
        label: 'Less social',
        text: "Fewer surf camps or hostels here — easier to end up surfing on your own",
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (
      prefs.tripVibe === 'party' &&
      !(dest.partyScene || hasVibe(dest, ['nightlife', 'party']))
    ) {
      return {
        key: 'limited-nightlife', priority: 45,
        label: 'Limited nightlife',
        text: 'Nightlife is limited — evenings stay pretty relaxed',
      };
    }
    return null;
  },

  (dest, prefs) => {
    if (
      prefs.tripVibe !== 'party' &&
      (dest.partyScene || hasVibe(dest, ['party', 'nightlife']))
    ) {
      return {
        key: 'busy-nights', priority: 40,
        label: 'Lively nights',
        text: 'The town can be lively at night if you like early dawn patrols',
      };
    }
    return null;
  },

  // NOTE: waterTemp deliberately excluded — no reliable per-destination
  // water_temp field exists on UiDestination. Add rules here when surfaced.
];

// --- Public API ----------------------------------------------------------

export interface ExplanationResult {
  positives: Reason[];
  tradeoffs: Reason[];
}

const MAX_POSITIVES = 5;
const MAX_TRADEOFFS = 3;

export const buildRecommendationExplanation = (
  dest: UiDestination,
  prefs: RecommendationPrefs,
  /** All 12 monthly-intelligence rows for this destination, if it's in the V1
   * research set. Omit (or pass []) for destinations outside that set --
   * every monthly-informed rule degrades to a no-op, not a guess. */
  monthlyRows: MonthlyIntelligenceRow[] = [],
  opts: { maxPositives?: number; maxTradeoffs?: number } = {},
): ExplanationResult => {
  const { maxPositives = MAX_POSITIVES, maxTradeoffs = MAX_TRADEOFFS } = opts;
  const ctx = deriveContext(dest, prefs, monthlyRows);

  const dedupe = (reasons: Reason[]): Reason[] => {
    const seen = new Set<string>();
    return reasons.filter((r) => {
      if (seen.has(r.key)) return false;
      seen.add(r.key);
      return true;
    });
  };

  const runRules = (rules: Rule[]): Reason[] =>
    dedupe(
      rules
        .map((rule) => {
          try {
            return rule(dest, prefs, ctx);
          } catch {
            return null;
          }
        })
        .filter((r): r is Reason => r !== null),
    ).sort((a, b) => b.priority - a.priority);

  const positives = runRules(positiveRules).slice(0, maxPositives);
  const tradeoffs = runRules(tradeoffRules).slice(0, maxTradeoffs);

  if (positives.length === 0) {
    positives.push({
      key: 'fallback', priority: 0,
      label: cap(dest.waveType),
      text: `A solid ${cap(dest.waveType)} destination worth a closer look`,
    });
  }

  return { positives, tradeoffs };
};
